import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Analytics as AnalyticsIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import api from '../../services/api';

const CaseAnalytics = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [analyticsDialog, setAnalyticsDialog] = useState({ open: false, case: null });
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'info' });

  useEffect(() => {
    loadCases();
    // This is the component's one-time initial data load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showAlert = (message, severity = 'info') => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity }), 5000);
  };

  const loadCases = async () => {
    try {
      const response = await api.get('/cases');
      setCases(response.data.cases || []);
    } catch (error) {
      console.error('Error loading cases:', error);
      showAlert('Failed to load cases', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCaseAnalytics = async (caseData) => {
    try {
      setLoadingAnalytics(true);
      const response = await api.get(`/admin/export/case-analytics/${caseData._id}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading case analytics:', error);
      showAlert('Failed to load case analytics', 'error');
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const handleViewAnalytics = (caseData) => {
    setAnalyticsDialog({ open: true, case: caseData });
    loadCaseAnalytics(caseData);
  };

  const handleExportCaseAnalytics = async (caseData) => {
    try {
      const response = await api.get(`/admin/export/case-analytics/${caseData._id}?format=csv`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `case-analytics-${caseData.title.replace(/[^a-zA-Z0-9]/g, '_')}-${Date.now()}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);

      showAlert('Case analytics exported successfully!', 'success');
    } catch (error) {
      console.error('Export failed:', error);
      showAlert('Export failed: ' + (error.response?.data?.message || error.message), 'error');
    }
  };

  const filteredCases = cases.filter(case_ =>
    case_.title.toLowerCase().includes(search.toLowerCase()) ||
    case_.presentingComplaint.toLowerCase().includes(search.toLowerCase()) ||
    case_.specialty.toLowerCase().includes(search.toLowerCase())
  );

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Core': return 'success';
      case 'Intermediate': return 'warning';
      case 'Advanced': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Case Analytics
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View detailed analytics and export data for individual cases.
      </Typography>

      {alert.show && (
        <Alert severity={alert.severity} sx={{ mb: 3 }}>
          {alert.message}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search cases by title, presenting complaint, or specialty..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>Case Title</TableCell>
              <TableCell>Presenting Complaint</TableCell>
              <TableCell>Specialty</TableCell>
              <TableCell>Difficulty</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCases.map((case_) => (
              <TableRow key={case_._id} hover>
                <TableCell>
                  <Typography variant="subtitle2">
                    {case_.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    by {case_.author?.firstName} {case_.author?.lastName}
                  </Typography>
                </TableCell>
                <TableCell>{case_.presentingComplaint}</TableCell>
                <TableCell>{case_.specialty}</TableCell>
                <TableCell>
                  <Chip
                    label={case_.difficulty}
                    color={getDifficultyColor(case_.difficulty)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box>
                    {case_.environments?.testing?.published && (
                      <Chip label="Testing" color="warning" size="small" sx={{ mr: 0.5 }} />
                    )}
                    {case_.environments?.production?.published && (
                      <Chip label="Production" color="primary" size="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    startIcon={<ViewIcon />}
                    onClick={() => handleViewAnalytics(case_)}
                    sx={{ mr: 1 }}
                  >
                    View
                  </Button>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleExportCaseAnalytics(case_)}
                  >
                    Export
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Case Analytics Dialog */}
      <Dialog
        open={analyticsDialog.open}
        onClose={() => setAnalyticsDialog({ open: false, case: null })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AnalyticsIcon sx={{ mr: 1 }} />
            Case Analytics: {analyticsDialog.case?.title}
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingAnalytics ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : analytics ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Usage Statistics</Typography>
                    <Typography variant="body2">
                      Total Completions: <strong>{analytics.usageStatistics.totalCompletions}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Unique Users: <strong>{analytics.usageStatistics.uniqueUsers}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Average Score: <strong>{analytics.usageStatistics.averageScore}%</strong>
                    </Typography>
                    <Typography variant="body2">
                      Average Duration: <strong>{analytics.usageStatistics.averageDuration} minutes</strong>
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Score Distribution</Typography>
                    <Typography variant="body2">
                      Excellent (≥90%): <strong>{analytics.usageStatistics.scoreDistribution.excellent}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Good (70-89%): <strong>{analytics.usageStatistics.scoreDistribution.good}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Satisfactory (50-69%): <strong>{analytics.usageStatistics.scoreDistribution.satisfactory}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Needs Improvement (&lt;50%): <strong>{analytics.usageStatistics.scoreDistribution.needsImprovement}</strong>
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Category Averages</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <Typography variant="body2" align="center">
                          History Questions<br />
                          <strong>{analytics.usageStatistics.categoryAverages.historyQuestions}%</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body2" align="center">
                          Flow<br />
                          <strong>{analytics.usageStatistics.categoryAverages.flow}%</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body2" align="center">
                          Examiner Questions<br />
                          <strong>{analytics.usageStatistics.categoryAverages.examinerQuestions}%</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body2" align="center">
                          Empathy<br />
                          <strong>{analytics.usageStatistics.categoryAverages.empathy}%</strong>
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Typography>No analytics data available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalyticsDialog({ open: false, case: null })}>
            Close
          </Button>
          {analytics && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => handleExportCaseAnalytics(analyticsDialog.case)}
            >
              Export Analytics
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CaseAnalytics;
